import {
  type ElementType,
  type JSXElementConstructor,
  type ReactNode,
  type Ref,
} from 'react';
import { GridActionsCellItem, GridSlotProps } from '@mui/x-data-grid-pro';

import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

export type DisabledTooltipMenuItemProps = GridSlotProps['baseMenuItem'] & {
  'data-disabled-reason'?: ReactNode;
  ref?: Ref<HTMLLIElement>;
};

export function DisabledTooltipMenuItem({
  children,
  className,
  disabled = false,
  iconEnd,
  iconStart,
  selected,
  style,
  value,
  ['data-disabled-reason']: disabledReason,
  ref,
  ...props
}: DisabledTooltipMenuItemProps) {
  const item = (
    <MenuItem
      ref={ref}
      disabled={disabled}
      selected={selected}
      className={className}
      style={style}
      value={value}
      {...props}
    >
      {iconStart ? <ListItemIcon>{iconStart}</ListItemIcon> : null}
      {children}
      {iconEnd ? (
        <Box
          component="span"
          sx={{ display: 'inline-flex', ml: 'auto', pl: 1 }}
        >
          {iconEnd}
        </Box>
      ) : null}
    </MenuItem>
  );

  if (!disabled || !disabledReason) {
    return item;
  }

  return (
    <Tooltip arrow title={disabledReason}>
      <Box component="span" sx={{ display: 'block' }}>
        {item}
      </Box>
    </Tooltip>
  );
}

type TooltipGridActionsCellItemProps = {
  showInMenu: true;
  closeMenuOnClick?: boolean;
  closeMenu?: () => void;
  label: ReactNode;
  icon?: JSXElementConstructor<unknown> | ReactNode;
  component?: ElementType;
  tooltipText?: ReactNode;
} & Omit<GridSlotProps['baseMenuItem'], 'component'>;

export function TooltipGridActionsCellItem({
  tooltipText,
  ...props
}: TooltipGridActionsCellItemProps) {
  return <GridActionsCellItem {...props} data-disabled-reason={tooltipText} />;
}
